/**
 * Marketing Images Utility
 * Maps marketing feature screenshots to language-specific image files
 * Supports fallback to English screenshots when language-specific versions aren't available
 */

import { logDebug } from '@/utils/errorHandler';

export interface MarketingImageMapping {
  [featureName: string]: string;
}

/**
 * Supported languages for marketing screenshots
 */
export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'pt'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * Base image mapping for marketing screenshots
 * Maps feature keys to their base filename (without language suffix)
 */
const MARKETING_IMAGE_MAP: MarketingImageMapping = {
  dataImport: 'import-validation',
  attributeFamily: 'attribute-family',
  bulkOperations: 'bulk-workflow',
  searchFilter: 'search-filter',
  analytics: 'analytics-dashboard',
};

/**
 * Check if a language is supported for marketing images
 * @param language - Language code to check
 * @returns boolean indicating if language is supported
 */
function isSupportedLanguage(language: string): language is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(language as SupportedLanguage);
}

/**
 * Get the correct marketing image path based on feature and language
 * @param feature - The feature key (e.g., 'dataImport', 'analytics')
 * @param language - Current language code (e.g., 'en', 'es', 'de', 'pt')
 * @returns Full path to the marketing screenshot
 */
export function getMarketingImage(feature: string, language: string = 'en'): string {
  const baseFilename = MARKETING_IMAGE_MAP[feature];

  if (!baseFilename) {
    logDebug('Marketing image not found for feature', {
      feature,
      availableFeatures: Object.keys(MARKETING_IMAGE_MAP),
      fallbackUsed: '/marketingScreenshots/analytics-dashboard_en.webp',
    });
    return '/marketingScreenshots/analytics-dashboard_en.webp'; // Default fallback
  }

  // Normalize language code
  const normalizedLanguage = language.toLowerCase().split('-')[0]; // Handle cases like 'en-US'

  // For now, always use English screenshots as requested
  // In the future when other language screenshots are available:
  // const targetLanguage = isSupportedLanguage(normalizedLanguage) ? normalizedLanguage : 'en'
  const targetLanguage = 'en';

  const imagePath = `/marketingScreenshots/${baseFilename}_${targetLanguage}.webp`;

  return imagePath;
}

/**
 * Get all available marketing images for a given language
 * @param language - Language code
 * @returns Object mapping feature names to image paths
 */
export function getAllMarketingImages(language: string = 'en'): Record<string, string> {
  const imageMap: Record<string, string> = {};

  Object.keys(MARKETING_IMAGE_MAP).forEach(feature => {
    imageMap[feature] = getMarketingImage(feature, language);
  });

  return imageMap;
}

/**
 * Check if a marketing image exists for a specific feature and language
 * @param feature - Feature key
 * @param language - Language code
 * @returns boolean indicating if image exists
 */
export function hasMarketingImage(feature: string, language: string = 'en'): boolean {
  return feature in MARKETING_IMAGE_MAP;
}

/**
 * Get the list of available marketing features
 * @returns Array of feature keys that have marketing images
 */
export function getAvailableMarketingFeatures(): string[] {
  return Object.keys(MARKETING_IMAGE_MAP);
}

/**
 * Validate if all required marketing images exist for a given language
 * This is useful for ensuring translation completeness
 * @param language - Language code to validate
 * @returns Object with validation results and missing images
 */
export function validateMarketingImages(language: string = 'en'): {
  isValid: boolean;
  missingImages: string[];
  availableImages: string[];
} {
  const features = getAvailableMarketingFeatures();
  const missing: string[] = [];
  const available: string[] = [];

  features.forEach(feature => {
    if (hasMarketingImage(feature, language)) {
      available.push(feature);
    } else {
      missing.push(feature);
    }
  });

  return {
    isValid: missing.length === 0,
    missingImages: missing,
    availableImages: available,
  };
}

/**
 * Get marketing image metadata for debugging and development
 * @param feature - Feature key
 * @param language - Language code
 * @returns Metadata object with image information
 */
export function getMarketingImageMeta(
  feature: string,
  language: string = 'en'
): {
  feature: string;
  language: string;
  baseFilename: string | null;
  fullPath: string;
  exists: boolean;
} {
  const baseFilename = MARKETING_IMAGE_MAP[feature] || null;

  return {
    feature,
    language,
    baseFilename,
    fullPath: getMarketingImage(feature, language),
    exists: hasMarketingImage(feature, language),
  };
}
